import type {
  TileStreamingSelectionProfile,
} from "../world/streaming/index.ts";
import { CAMERA_RENDER_CONFIG } from "./camera.ts";
import { ENVIRONMENT_RENDER_CONFIG } from "./environment.ts";
import {
  TILE_STREAMING_CONFIG,
  TILE_STREAMING_FLIGHT_PROFILE,
} from "./streaming.ts";

export type WorldViewMode = "grounded" | "flight";

export interface WorldViewProfileSettings {
  readonly streamedRadiusTiles: number;
  readonly fullDetailRadiusM: number;
  readonly cameraFarMeters: number;
  /**
   * Summer daytime visibility. Time-of-day and season retain their authored
   * proportional reductions.
   */
  readonly fogFarDayMeters: number;
}

export interface WorldViewSettings {
  readonly schemaVersion: 1;
  readonly grounded: WorldViewProfileSettings;
  readonly flight: WorldViewProfileSettings;
}

export type WorldViewSettingKey = keyof WorldViewProfileSettings;

export interface NumericSettingLimit {
  readonly min: number;
  readonly max: number;
  readonly step: number;
}

export const WORLD_VIEW_SETTINGS_STORAGE_KEY = "vilnius.world-view-settings.v1";

export const WORLD_VIEW_SETTING_LIMITS: Readonly<
  Record<WorldViewSettingKey, NumericSettingLimit>
> = Object.freeze({
  streamedRadiusTiles: Object.freeze({ min: 1, max: 7, step: 1 }),
  fullDetailRadiusM: Object.freeze({ min: 32, max: 192, step: 8 }),
  cameraFarMeters: Object.freeze({ min: 300, max: 1_800, step: 10 }),
  fogFarDayMeters: Object.freeze({ min: 150, max: 1_800, step: 2 }),
});

export const DEFAULT_WORLD_VIEW_SETTINGS: Readonly<WorldViewSettings> =
  deepFreezeSettings({
    schemaVersion: 1,
    grounded: {
      streamedRadiusTiles:
        TILE_STREAMING_CONFIG.lodRings.distantRadiusTiles,
      fullDetailRadiusM: TILE_STREAMING_CONFIG.fullDetailRadiusM,
      cameraFarMeters: CAMERA_RENDER_CONFIG.farMeters,
      fogFarDayMeters: ENVIRONMENT_RENDER_CONFIG.fogFarDayMeters,
    },
    flight: {
      streamedRadiusTiles:
        TILE_STREAMING_FLIGHT_PROFILE.lodRings.distantRadiusTiles,
      fullDetailRadiusM: TILE_STREAMING_FLIGHT_PROFILE.fullDetailRadiusM,
      cameraFarMeters: CAMERA_RENDER_CONFIG.flightFarMeters,
      fogFarDayMeters: Math.min(
        CAMERA_RENDER_CONFIG.flightFarMeters,
        ENVIRONMENT_RENDER_CONFIG.fogFarDayMeters *
          ENVIRONMENT_RENDER_CONFIG.flightFogDistanceScale,
      ),
    },
  });

function deepFreezeSettings(
  settings: WorldViewSettings,
): Readonly<WorldViewSettings> {
  Object.freeze(settings.grounded);
  Object.freeze(settings.flight);
  return Object.freeze(settings);
}

function finiteNumber(
  value: unknown,
  fallback: number,
): number {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : fallback;
}

function normalizeSettingValue(
  key: WorldViewSettingKey,
  value: unknown,
  fallback: number,
): number {
  const limits = WORLD_VIEW_SETTING_LIMITS[key];
  const finite = finiteNumber(value, fallback);
  const stepped = Math.round(finite / limits.step) * limits.step;
  return Math.min(limits.max, Math.max(limits.min, stepped));
}

function recordValue(
  value: unknown,
  key: WorldViewSettingKey,
): unknown {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

function normalizeProfile(
  value: unknown,
  fallback: Readonly<WorldViewProfileSettings>,
): WorldViewProfileSettings {
  const cameraFarMeters = normalizeSettingValue(
    "cameraFarMeters",
    recordValue(value, "cameraFarMeters"),
    fallback.cameraFarMeters,
  );
  return {
    streamedRadiusTiles: normalizeSettingValue(
      "streamedRadiusTiles",
      recordValue(value, "streamedRadiusTiles"),
      fallback.streamedRadiusTiles,
    ),
    fullDetailRadiusM: normalizeSettingValue(
      "fullDetailRadiusM",
      recordValue(value, "fullDetailRadiusM"),
      fallback.fullDetailRadiusM,
    ),
    cameraFarMeters,
    // Rendering past the camera plane would produce a hard horizon before the
    // requested atmosphere, so the persisted profile enforces this invariant.
    fogFarDayMeters: Math.min(
      cameraFarMeters,
      normalizeSettingValue(
        "fogFarDayMeters",
        recordValue(value, "fogFarDayMeters"),
        fallback.fogFarDayMeters,
      ),
    ),
  };
}

export function normalizeWorldViewSettings(
  value: unknown,
): WorldViewSettings {
  const record = typeof value === "object" && value !== null
    ? value as Record<string, unknown>
    : {};
  if (
    record.schemaVersion !== undefined &&
    record.schemaVersion !== 1
  ) {
    return {
      schemaVersion: 1,
      grounded: { ...DEFAULT_WORLD_VIEW_SETTINGS.grounded },
      flight: { ...DEFAULT_WORLD_VIEW_SETTINGS.flight },
    };
  }
  return {
    schemaVersion: 1,
    grounded: normalizeProfile(
      record.grounded,
      DEFAULT_WORLD_VIEW_SETTINGS.grounded,
    ),
    flight: normalizeProfile(
      record.flight,
      DEFAULT_WORLD_VIEW_SETTINGS.flight,
    ),
  };
}

export function parseWorldViewSettings(serialized: string): WorldViewSettings {
  try {
    return normalizeWorldViewSettings(JSON.parse(serialized));
  } catch {
    return normalizeWorldViewSettings(undefined);
  }
}

export function loadWorldViewSettings(
  storage: Pick<Storage, "getItem">,
): WorldViewSettings {
  try {
    const stored = storage.getItem(WORLD_VIEW_SETTINGS_STORAGE_KEY);
    return stored === null
      ? normalizeWorldViewSettings(DEFAULT_WORLD_VIEW_SETTINGS)
      : parseWorldViewSettings(stored);
  } catch {
    return normalizeWorldViewSettings(DEFAULT_WORLD_VIEW_SETTINGS);
  }
}

export function persistWorldViewSettings(
  storage: Pick<Storage, "setItem">,
  settings: Readonly<WorldViewSettings>,
): boolean {
  try {
    storage.setItem(
      WORLD_VIEW_SETTINGS_STORAGE_KEY,
      JSON.stringify(normalizeWorldViewSettings(settings)),
    );
    return true;
  } catch {
    return false;
  }
}

export function updateWorldViewSetting(
  settings: Readonly<WorldViewSettings>,
  mode: WorldViewMode,
  key: WorldViewSettingKey,
  value: number,
): WorldViewSettings {
  const nextProfile = {
    ...settings[mode],
    [key]: value,
  };
  return normalizeWorldViewSettings({
    ...settings,
    [mode]: nextProfile,
  });
}

export function resetWorldViewProfile(
  settings: Readonly<WorldViewSettings>,
  mode: WorldViewMode,
): WorldViewSettings {
  return normalizeWorldViewSettings({
    ...settings,
    [mode]: DEFAULT_WORLD_VIEW_SETTINGS[mode],
  });
}

export function streamedTileCapacity(radiusTiles: number): number {
  const diameter = radiusTiles * 2 + 1;
  return diameter * diameter;
}

export function streamingProfileFromViewSettings(
  profile: Readonly<WorldViewProfileSettings>,
): TileStreamingSelectionProfile {
  const radius = profile.streamedRadiusTiles;
  return {
    maxCachedTiles: streamedTileCapacity(radius),
    fullDetailRadiusM: profile.fullDetailRadiusM,
    lodRings: {
      fullRadiusTiles: 0,
      simplifiedRadiusTiles: Math.min(1, radius),
      distantRadiusTiles: radius,
    },
  };
}
